const FIRST_LANDING_URL_KEY = 'first_landing_url'

// Credential-bearing query params that must never be persisted or forwarded to
// the backend. `reset_password` is this app's own password-reset hash (see
// root.tsx); the rest are common OAuth / token param names.
const SENSITIVE_PARAMS = [
  'reset_password',
  'reset_token',
  'token',
  'code',
  'access_token',
  'id_token',
  'auth',
  'password',
]

// Strip sensitive query params from a URL so they are never written to
// localStorage or sent to the backend as meta.landing_url. Falls back to the
// raw value if the URL cannot be parsed.
const sanitizeUrl = (rawUrl: string): string => {
  try {
    const url = new URL(rawUrl)
    SENSITIVE_PARAMS.forEach(param => url.searchParams.delete(param))
    return url.toString()
  } catch (error) {
    return rawUrl
  }
}

// Capture the full landing URL (including all query params) on the user's first
// visit only. Subsequent calls are a no-op, so in-app navigation never
// overwrites the original value. Must run as early as possible (before the
// router can redirect, e.g. protectRoute -> /register) to record the genuine
// landing URL.
//
// NOTE: Known credential-bearing query params (reset_password hashes, OAuth
// tokens, etc.) are stripped client-side via sanitizeUrl as defence-in-depth.
// The backend should still strip sensitive fields from meta.landing_url before
// storage, since the client-side list is best-effort and not exhaustive.
export const captureFirstLandingUrl = () => {
  try {
    if (localStorage.getItem(FIRST_LANDING_URL_KEY)) return
    localStorage.setItem(
      FIRST_LANDING_URL_KEY,
      sanitizeUrl(window.location.href),
    )
  } catch (error) {
    // localStorage may be unavailable (private mode / storage disabled)
  }
}

export const getFirstLandingUrl = (): string => {
  try {
    return localStorage.getItem(FIRST_LANDING_URL_KEY) ?? ''
  } catch (error) {
    return ''
  }
}

// Reset the stored landing URL (e.g. after a successful registration) so a
// later first visit captures a fresh value.
export const clearFirstLandingUrl = () => {
  try {
    localStorage.removeItem(FIRST_LANDING_URL_KEY)
  } catch (error) {
    // localStorage may be unavailable (private mode / storage disabled)
  }
}
